<!-- TEMPLATE -->
<template>
  <BadgeList :items="badges" />
</template>
<!-- SCRIPT -->
<script lang="ts" setup>
import { computed, toRefs } from 'vue'
import { mergeIntervals } from '@/utils/merge-intervals'
import { intervalToLabel } from '@/intervalToLabel'
import { useI18n } from 'vue-i18n'
import {
  BuildingProperties,
  ComplexProperties,
  LevelProperties,
  RoomCapacity,
  RoomProperties,
  RoomTypeFilter,
  roomTypeFiltersToRoomType
} from '../../../types'
import BadgeList from '@/design-system/atoms/BadgeList.vue'
import { COCLASSGROUPS } from '@/interfaces/coclass.interface'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { useButtonTextFormatter } from './composables'
import { LOCALES } from '@/utils/locales'
import { PROJECT_STATUS, PROJECT_TYPE } from '@/api-client/models/project/Constants'
import { RealEstateProperties } from '@/components/types'
import { useInformation } from '@/pinia/information'
import { AnyInformationResponse } from '@/api-client/models/shared/Contracts'

type BadgeListItem = {
  text: string
  color?: string
  children?: BadgeListItem[]
}
type Props = {
  showTimeSelection: boolean
  fullscreen: boolean
}
const props = defineProps<Props>()
const { showTimeSelection, fullscreen } = toRefs(props)
const { t, locale } = useI18n()
const { buttonText: timeSelectionText } = useButtonTextFormatter(LOCALES[locale.value])

const filters = useCurrentFeatureFilters()
const information = useInformation()
const submittedInformation = computed(() => information.submitted.data)

const badges = computed<BadgeListItem[]>(() => {
  const items: BadgeListItem[] = []
  const submitted = filters.value?.submitted
  if (!submitted) return items
  if (fullscreen.value && submittedInformation.value)
    items.push(makeInformationItems(submittedInformation.value))
  if (submitted.complex) items.push(makeComplexItems(submitted.complex))
  if ('realEstate' in submitted && submitted.realEstate)
    items.push(makeRealEstateItems(submitted.realEstate))
  if (submitted.buildings) items.push(...makeConstructionItems(submitted.buildings))
  if ('level' in submitted && submitted.level) items.push(makeLevelItems(submitted.level))
  if ('room' in submitted && submitted.room) items.push(makeRoomItems(submitted.room.properties))
  if ('roomTypes' in submitted && submitted.roomTypes && !submitted.room)
    items.push(...makeRoomTypeItems(submitted.roomTypes))
  if ('projectTypes' in submitted && submitted.projectTypes)
    items.push(...makeProjectTypeItems(submitted.projectTypes))
  if ('projectStatuses' in submitted && submitted.projectStatuses)
    items.push(...makeProjectStatusItems(submitted.projectStatuses))
  if ('ticketTypes' in submitted && submitted.ticketTypes)
    items.push(...makeTicketTypeItems(submitted.ticketTypes))
  if ('ticketStatuses' in submitted && submitted.ticketStatuses)
    items.push(...makeTicketStatusItems(submitted.ticketStatuses))
  if ('measurementTags' in submitted && submitted.measurementTags) {
    for (const tag in submitted.measurementTags) {
      items.push(...makeAggregateItems(submitted.measurementTags[tag]))
    }
  }
  if ('roomCapacity' in submitted && submitted.roomCapacity && !submitted.room)
    items.push(...makeRoomCapacityItems(submitted.roomCapacity))
  if ('onlyBookable' in submitted && submitted.onlyBookable && !submitted.room)
    items.push(makeOnlyBookableItems())
  if ('year' in submitted && submitted.year) {
    if (!fullscreen.value) items.push(makeYearTypeItems(submitted.year))
  }
  if ('budget' in submitted && submitted.budget) {
    items.push({
      text: `${submitted.budget.from} - ${submitted.budget.to} tkr`,
      color: '#d5d7f6'
    })
  }
  if (showTimeSelection.value && submitted.type !== 'building_score')
    items.push(makeTimeSelectionItems())
  return items
})

function makeYearTypeItems(year: number): BadgeListItem {
  return {
    text: `${year}`,
    color: '#d5d7f6'
  }
}

function makeComplexItems(complex: ComplexProperties): BadgeListItem {
  return {
    text: complex.title
  }
}
function makeRealEstateItems(realEstate: RealEstateProperties): BadgeListItem {
  return {
    text: realEstate.displayName
  }
}
function makeProjectTypeItems(project: string[]): BadgeListItem[] {
  if (project.length > 0 && project.length < 3) {
    return project.map((x) => ({
      text: PROJECT_TYPE[x][locale.value],
      color: '#d5d7f6'
    }))
  } else if (project.length > 0) {
    const children: BadgeListItem[] = project.map((x) => ({
      text: PROJECT_TYPE[x][locale.value]
    }))
    return [
      {
        text: `${project.length} ${t('general__text__dropdown_projecttypes').toLowerCase()}`,
        color: '#d5d7f6',
        children
      }
    ]
  }
  return []
}
function makeProjectStatusItems(project: string[]): BadgeListItem[] {
  if (project.length > 0 && project.length < 3) {
    return project.map((x) => ({
      text: PROJECT_STATUS[x][locale.value],
      color: '#d5d7f6'
    }))
  } else if (project.length > 0) {
    const children: BadgeListItem[] = project.map((x) => ({
      text: PROJECT_STATUS[x][locale.value]
    }))
    return [
      {
        text: `${project.length} ${t('general__text__dropdown_projectstatuses').toLowerCase()}`,
        color: '#d5d7f6',
        children
      }
    ]
  }
  return []
}

function makeTicketTypeItems(selection: string[]): BadgeListItem[] {
  if (selection.length > 0 && selection.length < 3) {
    return selection.map((x) => ({
      text: t(x),
      color: '#d5d7f6'
    }))
  } else if (selection.length > 0) {
    const children: BadgeListItem[] = selection.map((x) => ({
      text: t(x)
    }))
    return [
      {
        text: `${selection.length} ${t('general__text__types').toLowerCase()}`,
        color: '#d5d7f6',
        children
      }
    ]
  }
  return []
}

function makeAggregateItems(selection: string[]): BadgeListItem[] {
  if (selection.length > 0 && selection.length < 3) {
    return selection.map((x) => ({
      text: t(x),
      color: '#d5d7f6'
    }))
  } else if (selection.length > 0) {
    const children: BadgeListItem[] = selection.map((x) => ({
      text: t(x)
    }))
    return [
      {
        text: `${selection.length} ${t('dashboard__text__measurement_tags').toLowerCase()}`,
        color: '#d5d7f6',
        children
      }
    ]
  }
  return []
}

function makeTicketStatusItems(selection: string[]): BadgeListItem[] {
  if (selection.length > 0 && selection.length < 3) {
    return selection.map((x) => ({
      text: t(x),
      color: '#d5d7f6'
    }))
  } else if (selection.length > 0) {
    const children: BadgeListItem[] = selection.map((x) => ({
      text: t(x)
    }))
    return [
      {
        text: `${selection.length} ${t('general__text__statuses').toLowerCase()}`,
        color: '#d5d7f6',
        children
      }
    ]
  }
  return []
}

function makeConstructionItems(buildings: BuildingProperties[]): BadgeListItem[] {
  if (buildings.length > 0 && buildings.length < 3) {
    return buildings.map((x) => ({
      text: x.displayName
    }))
  } else if (buildings.length > 0) {
    const children: BadgeListItem[] = buildings.map((x) => ({
      text: x.displayName
    }))
    return [
      {
        text: `${buildings.length} ${t('general__data__buildings').toLowerCase()}`,
        children
      }
    ]
  }
  return []
}
function makeTimeSelectionItems() {
  return {
    text: timeSelectionText.value,
    color: '#d5d7f6'
  }
}

function makeLevelItems(level: LevelProperties): BadgeListItem {
  return {
    text: `${t('general__text__level')} ${level.level}`
  }
}
function makeRoomItems(room: RoomProperties): BadgeListItem {
  return {
    text: room.displayName
  }
}

function makeInformationItems(informationResponse: AnyInformationResponse): BadgeListItem {
  const buildings =
    'buildings' in informationResponse
      ? informationResponse.buildings
      : 'numberOfConstructions' in informationResponse
      ? informationResponse.numberOfConstructions
      : 'numberOf' in informationResponse
      ? informationResponse.numberOf['CONSTRUCTION']
      : '?'
  const rooms =
    'spaces' in informationResponse
      ? informationResponse.spaces
      : 'numberOfSpaces' in informationResponse
      ? informationResponse.numberOfSpaces
      : 'numberOf' in informationResponse
      ? informationResponse.numberOf['SPACE']
      : '?'
  return {
    text: `${buildings} ${t('general__data__buildings').toLocaleLowerCase()} ${t(
      'general__text__and'
    ).toLocaleLowerCase()} ${rooms} ${t('general__data__rooms').toLocaleLowerCase()}`
  }
}

function makeRoomTypeItems(roomTypes: RoomTypeFilter[]): BadgeListItem[] {
  const groupedCoclasses = new Set(roomTypeFiltersToRoomType(roomTypes).map((x) => x.roomGroup))
  const groups = Array.from(groupedCoclasses)
  if (groups.length > 0 && groups.length < 3) {
    return groups.map((x) => ({
      text: COCLASSGROUPS[x][locale.value],
      color: '#d5d7f6'
    }))
  } else if (groups.length > 0) {
    const children: BadgeListItem[] = groups.map((x) => ({
      text: COCLASSGROUPS[x][locale.value]
    }))
    return [
      {
        text: `${groups.length} ${t('general__text__dropdown_roomtypes').toLowerCase()}`,
        color: '#d5d7f6',
        children
      }
    ]
  }
  return []
}

function makeRoomCapacityItems(selectedRoomCapacity: RoomCapacity[]): BadgeListItem[] {
  const intervals = mergeIntervals(selectedRoomCapacity.map((x) => x.value).flat())
  if (intervals.length === 0) {
    return []
  }

  return intervals.map(intervalToLabel(t)).map((label) => ({
    text: `${label} ${t('general__data__room__capacity-seats').toLowerCase()}`,
    color: '#d5d7f6'
  }))
}

function makeOnlyBookableItems(): BadgeListItem {
  return {
    text: t('general__text__dropdown_bookable_rooms'),
    color: '#d5d7f6'
  }
}
</script>
<!-- STYLING -->
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
ul {
  display: flex;
  flex-direction: row;
  min-height: calc(16px + 4px + 4px);
  margin: 0;
  padding: 0;
  gap: 5px;
  flex-wrap: wrap;
  align-items: center;
}
li {
  display: inline-block;
  padding: 4px;
  border-radius: 4px;
  &.gray {
    background-color: variables.$color-graph-gray;
  }
  &.primary {
    background-color: variables.$color-primary-light;
  }
  &.multiselect {
    color: variables.$color-pure-white;
    background-color: variables.$color-greyscale-secondary;
  }
}

.itemgroup {
  display: flex;
  .inline {
    display: inline-block;
    &:not(:first-child) {
      margin-left: 5px;
    }
  }
  .vertical-separator {
    display: inline-block;
    height: 16px;
    width: 1px;
    margin-left: 5px;
    background-color: variables.$color-graph-gray;
    transform: translateY(4px);
  }

  &:last-child {
    .vertical-separator {
      background-color: transparent;
    }
  }
}

span {
  @include font-mixins.desktop-regular-label;
}
</style>
